import React, { useCallback, useEffect, useState } from "react";
import {
  useAuth,
  useI18N,
  useSyncBusinessInterest,
  VenueDetailsBySlugResponse,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";

import Loader from "@/components/common/Loader";
import { useCustomToast } from "@/hooks/useCustomToast";
import { XIcon } from "lucide-react";
import WideButton from "@/components/common/WideButton";
import { useDarkMode } from "@/hooks/useDarkMode";
import AnalyticsService from "@/services/analytics";
import { cn } from "@/lib/utils";
import { StandardButton } from "@/components/common/StandardButton";
import { BusinessInterestTypeSmall } from "common/types";

function AddInterest(props: {
  venueDetailsResponse: VenueDetailsBySlugResponse;
  onClose: (isSuccess?: boolean) => void;
  onInfoClick: () => void;
  onRefresh?: () => void;
}) {
  const i18n = useI18N();
  const { user, isLoggedIn } = useAuth();
  const { modalStyle, modalClassName } = useModalHeight();
  const showToast = useCustomToast();

  const {
    data: mutateResult,
    error,
    isLoading,
    mutate,
  } = useSyncBusinessInterest();
  const isDark = useDarkMode();

  const [selectedInterestType, setSelectedInterestType] = useState<
    BusinessInterestTypeSmall | undefined
  >(undefined);

  // Zoom awareness state and logic
  const isZoomedIn = useZoomDetection();

  useEffect(() => {
    if (error) {
      AnalyticsService.trackGAEventHandler("addInterestError");
      AnalyticsService.trackMx("addInterestError", {
        VenueId: props.venueDetailsResponse.details.id,
        VenueBrandId: props.venueDetailsResponse.details.brandId,
        VenueBrandName: props.venueDetailsResponse.details.brandName,
        VenueAddress: props.venueDetailsResponse.details.address,
        VenueName: props.venueDetailsResponse.details.name,
        VenueTypes: props.venueDetailsResponse.details.types,
      });

      showToast(error.message, "error");
    }
  }, [error]);

  useEffect(() => {
    if (mutateResult) {
      console.log("mutateResult", mutateResult);
      if (mutateResult.status === "success") {
        showToast(mutateResult.message, "success");
        setSelectedInterestType(undefined);

        // Call onRefresh to update venue details if provided
        if (props.onRefresh) {
          console.log("Calling onRefresh after successful interest submission");
          setTimeout(() => {
            props.onRefresh();
          }, 1000);
        }

        props.onClose(true); // Pass true for successful submission
      } else {
        showToast(mutateResult.message, "error");
        setSelectedInterestType(undefined);
        props.onClose(false); // Pass false for failed submission
      }
    }
  }, [mutateResult]);

  const submit = useCallback(() => {
    AnalyticsService.trackGAEventHandler("submittedUserInterest");
    AnalyticsService.trackMx("submittedUserInterest", {
      VenueId: props.venueDetailsResponse.details.id,
      VenueBrandId: props.venueDetailsResponse.details.brandId,
      VenueBrandName: props.venueDetailsResponse.details.brandName,
      VenueAddress: props.venueDetailsResponse.details.address,
      VenueName: props.venueDetailsResponse.details.name,
      VenueTypes: props.venueDetailsResponse.details.types,
      ReviewerName: user?.name,
    });

    // Store the venue ID in localStorage if user is not logged in
    if (!isLoggedIn) {
      try {
        const venueId =
          props.venueDetailsResponse.details.business.id.toString();
        const storedInterestsStr =
          localStorage.getItem("interested_venues") || "[]";
        const storedInterests = JSON.parse(storedInterestsStr);

        if (!storedInterests.includes(venueId)) {
          storedInterests.push(venueId);
          localStorage.setItem(
            "interested_venues",
            JSON.stringify(storedInterests),
          );
        }
      } catch (e) {
        console.error("Error updating interested venues in localStorage", e);
      }
    }

    mutate({
      businessId:
        props.venueDetailsResponse.details?.business?.id.toString() || "",
      interestType: selectedInterestType?.value || "",
      notifyAccessibilityInformation: true,
      notifyLiveAlert: false,
      action: "create",
    });
  }, [
    props.venueDetailsResponse.details,
    user?.name,
    isLoggedIn,
    mutate,
    selectedInterestType?.value,
  ]);

  const isSubmitDisabled = () => {
    return !selectedInterestType;
  };

  return (
    <>
      <section style={modalStyle} className={cn(modalClassName)} role="none">
        {isLoading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex justify-center items-start pt-[250px] z-50">
            <Loader />
          </div>
        )}

        <header
          className={`py-4 min-h-14 flex items-center justify-between text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"}`}
        >
          <StandardButton
            onClick={() => {
              // Track cancellation
              AnalyticsService.trackGAEventHandler("cancelledUserInterest");
              AnalyticsService.trackMx("cancelledUserInterest", {
                VenueId: props.venueDetailsResponse.details.id,
                VenueBrandId: props.venueDetailsResponse.details.brandId,
                VenueBrandName: props.venueDetailsResponse.details.brandName,
                VenueAddress: props.venueDetailsResponse.details.address,
                VenueName: props.venueDetailsResponse.details.name,
                VenueTypes: props.venueDetailsResponse.details.types,
                ReviewerName: user?.name,
              });
              props.onClose();
            }}
            variant="closeModal"
            icon={<XIcon color={isDark ? "#A39FE8" : "#352FC3"} />}
            aria-label="Close modal"
            tabIndex={0}
            className="flex-shrink-0"
          />
          <h1 className="title-s-semibold text-center text-fg-neutral-strong flex-1 px-4">
            {i18n.get("submitInterest")}
          </h1>
          <div className="flex-shrink-0 w-10"></div>
          {" "}
          {/* Spacer to balance the layout */}
        </header>

        <div
          aria-label="Modal Content"
          className="flex-grow overflow-y-auto py-3"
          role="none"
        >
          <div className="self-stretch px-6 flex-col justify-start items-center gap-2 flex">
            <div className="self-stretch h-30 opacity-80 flex-col justify-start items-start gap-1 flex">
              <div className="self-stretch text-fg-neutral-strong title-s-semibold">
                {i18n.get(
                  "wouldYouOrSomeoneYouKnowLikeToLearnMoreAboutAccessibilityInformationFor",
                )}
              </div>

              <div className="self-stretch min-w-11 inline-flex flex-col justify-center items-center">
                <div className="self-stretch !text-fg-brand-subtle title-s-semibold">
                  {props.venueDetailsResponse.details.name}
                </div>
              </div>

              <div className="self-stretch py-2 inline-flex flex-col justify-start items-start gap-2.5 w-full">
                <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
              </div>
            </div>
            <div className="self-stretch rounded-lg justify-start items-center gap-8">
              <div className="flex flex-col">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  {props.venueDetailsResponse.details.businessInterestsTypes?.map(
                    (option, index) => (
                      <StandardButton
                        key={index}
                        type="button"
                        variant="tag"
                        size="sm"
                        onClick={() => {
                          setSelectedInterestType(option);
                        }}
                        className={cn(
                          "label-m-medium",
                          selectedInterestType?.value === option.value
                            ? "bg-[#FFBE74] border-[#FFBE74] text-fg-neutral-strong"
                            : "bg-container-brand-minimal border-border-token-brand-minimal text-fg-neutral-strong",
                        )}
                      >
                        {option.name}
                      </StandardButton>
                    ),
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <footer
          className={`py-3 min-h-14 flex items-center justify-center text-center px-6 border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky bottom-0 z-10" : ""}`}
        >
          <div className="flex-col justify-end items-center gap-4 flex md:w-full">
            <div className="w-full justify-center items-center inline-flex">
              <WideButton
                disabled={isSubmitDisabled()} // Disable if conditions are not met
                className="grow"
                onClick={() => {
                  submit();
                }}
                aria-label="Submit Interest"
              >
                {i18n.get("submit")}
              </WideButton>
            </div>
          </div>
        </footer>
      </section>
    </>
  );
}

export default AddInterest;
